import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, Plus, Search, Pin, Star, Trash2, Clock, X, BarChart2, Sparkles, Cloud, TrendingUp, Zap } from 'lucide-react';
import { useWorkspaces, type Workspace, type Widget } from '../hooks/useWorkspaces';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { cn } from '../components/ui/Button';
const TEMPLATES: {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  widgets: Widget[];
  counter: number;
}[] = [{
  id: 'meteorologist',
  name: 'Meteorologist',
  description: 'Weather analysis with maps, charts, and station data',
  icon: <Cloud size={18} className="text-[#EC4899]" data-id="element-2728" />,
  counter: 5,
  widgets: [{
    i: 'map-1',
    x: 0,
    y: 0,
    w: 8,
    h: 6,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'chart-1',
    x: 8,
    y: 0,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 8,
    y: 3,
    w: 4,
    h: 3,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 6,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 6,
    w: 6,
    h: 4,
    type: 'chart'
  }]
}, {
  id: 'short-term-trader',
  name: 'Short Term Trader',
  description: 'Intraday price signals, demand forecasts, and model runs',
  icon: <Zap size={18} className="text-[#EC4899]" data-id="element-2729" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 4,
    y: 0,
    w: 8,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map'
  }]
}, {
  id: 'energy-analyst',
  name: 'Energy Analyst',
  description: 'Long-range forecasts, model comparisons, and reporting',
  icon: <BarChart2 size={18} className="text-[#EC4899]" data-id="element-2730" />,
  counter: 5,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 6,
    y: 0,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 8,
    h: 4,
    type: 'table'
  }, {
    i: 'text-1',
    x: 8,
    y: 4,
    w: 4,
    h: 4,
    type: 'text',
    config: {
      prompt: 'Add your analysis notes here...'
    }
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 12,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }]
}, {
  id: 'energy-trader',
  name: 'Energy Trader',
  description: 'Multi-market price monitoring, generation mix, and live positions',
  icon: <TrendingUp size={18} className="text-[#EC4899]" data-id="element-2731" />,
  counter: 7,
  widgets: [{
    i: 'chart-1',
    x: 0,
    y: 0,
    w: 5,
    h: 4,
    type: 'chart'
  }, {
    i: 'chart-2',
    x: 5,
    y: 0,
    w: 7,
    h: 4,
    type: 'chart'
  }, {
    i: 'table-1',
    x: 0,
    y: 4,
    w: 6,
    h: 4,
    type: 'table'
  }, {
    i: 'chart-3',
    x: 6,
    y: 4,
    w: 6,
    h: 4,
    type: 'chart'
  }, {
    i: 'map-1',
    x: 0,
    y: 8,
    w: 8,
    h: 5,
    type: 'map',
    config: {
      overlay: 'Geopotential Height 500hPa'
    }
  }, {
    i: 'text-1',
    x: 8,
    y: 8,
    w: 4,
    h: 5,
    type: 'text',
    config: {
      prompt: 'Log positions and market observations here...'
    }
  }, {
    i: 'chart-4',
    x: 0,
    y: 13,
    w: 12,
    h: 4,
    type: 'chart'
  }]
}];
export function WorkspacesOverviewView() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    workspaces,
    createWorkspace,
    deleteWorkspace,
    togglePin,
    toggleFavorite,
    setActiveWorkspace,
    updateWidgets,
    updateWidgetsById,
    renameWorkspace
  } = useWorkspaces();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'pinned' | 'favorites'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'choose' | 'template'>('choose');
  // Auto-open modal when navigated here with openModal state
  useEffect(() => {
    if ((location.state as any)?.openModal) {
      setModalStep('choose');
      setModalOpen(true);
      // Clear the state so refreshing doesn't re-open
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const filteredWorkspaces = workspaces.filter(w => {
    if (filter === 'pinned' && !w.pinned) return false;
    if (filter === 'favorites' && !w.favorited) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return w.name.toLowerCase().includes(query) || w.description.toLowerCase().includes(query);
    }
    return true;
  }).sort((a, b) => {
    // Pinned first, then updated recently
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
  const handleCreate = () => {
    setModalStep('choose');
    setModalOpen(true);
  };
  const handleNewBlank = () => {
    setModalOpen(false);
    const newId = createWorkspace();
    navigate(`/workspace/${newId}`, {
      state: {
        renaming: true
      }
    });
  };
  const handleUseTemplate = (template: (typeof TEMPLATES)[number]) => {
    setModalOpen(false);
    // Short Term Trader → pin the Intraday Trader page to the sidebar and navigate there
    if (template.id === 'short-term-trader') {
      try {
        const existing = JSON.parse(localStorage.getItem('jua-pinned-pages') || '[]');
        if (!existing.includes('intraday')) {
          localStorage.setItem('jua-pinned-pages', JSON.stringify([...existing, 'intraday']));
        }
      } catch {}
      navigate('/intraday');
      return;
    }
    // Generate a unique name: "Meteorologist", "Meteorologist 2", "Meteorologist 3", etc.
    const baseName = template.name;
    const existingNames = new Set(workspaces.map(w => w.name));
    let uniqueName = baseName;
    let suffix = 2;
    while (existingNames.has(uniqueName)) {
      uniqueName = `${baseName} ${suffix}`;
      suffix++;
    }
    const newId = createWorkspace();
    setTimeout(() => {
      updateWidgetsById(newId, template.widgets, template.counter);
      renameWorkspace(newId, uniqueName);
      navigate(`/workspace/${newId}`);
    }, 0);
  };
  const handleCardClick = (id: string) => {
    setActiveWorkspace(id);
    navigate(`/workspace/${id}`);
  };
  return <div className="jua-workspaces-overview jua-page-bg min-h-screen text-slate-100 p-6 md:p-8" data-id="element-2732">
      <div className="max-w-7xl mx-auto space-y-8" data-id="element-2733">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2734">
          <div data-id="element-2735">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-2736">
              <LayoutGrid className="text-[#EC4899]" data-id="element-2737" />
              Workspaces
            </h1>
            <p className="text-slate-400 mt-1" data-id="element-2738">
              Manage and organize your trading dashboards
            </p>
          </div>
          <Button onClick={handleCreate} className="gap-2" data-id="element-2739">
            <Plus size={16} data-id="element-2740" />
            New Workspace
          </Button>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between jua-ws-filter-bar bg-white/[0.04] backdrop-blur-2xl p-2 rounded-xl border border-white/[0.12] shadow-xl shadow-black/20" style={{
        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
        backdropFilter: 'blur(40px) saturate(180%)'
      }} data-id="element-2741">
          <div className="jua-ws-filter-tabs flex items-center gap-0 border-b border-white/[0.06]" data-id="element-2742">
            {(['all', 'pinned', 'favorites'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={cn('px-4 py-2 text-sm font-medium transition-all capitalize relative', filter === f ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#EC4899] after:rounded-full' : 'text-slate-500 hover:text-slate-300')} data-id="element-2743">
                {f}
              </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-2744">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-2745" />
            <input type="text" placeholder="Search workspaces..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="jua-ws-search w-full bg-white/[0.06] border border-white/[0.10] rounded-lg pl-9 pr-4 py-1.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-[#EC4899]/50 focus:ring-1 focus:ring-[#EC4899]/20 transition-all backdrop-blur-sm" data-id="element-2746" />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2747">
          {filteredWorkspaces.map(ws => <div key={ws.id} onClick={() => handleCardClick(ws.id)} className="jua-ws-card group relative flex flex-col bg-slate-800/40 hover:bg-slate-800/80 border border-slate-700/50 hover:border-slate-600 transition-all rounded-xl p-5 cursor-pointer h-[200px]" data-id="element-2748">
              {/* Top Actions */}
              <div className="flex items-start justify-between mb-3" data-id="element-2749">
                <div className="w-9 h-9 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center text-[#EC4899]" data-id="element-2750">
                  <LayoutGrid size={16} data-id="element-2751" />
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2752">
                  <button onClick={e => {
                e.stopPropagation();
                togglePin(ws.id);
              }} className={cn('p-1.5 rounded-md transition-colors', ws.pinned ? 'text-[#EC4899] bg-[#EC4899]/10' : 'text-slate-400 hover:text-white hover:bg-white/[0.1]')} title={ws.pinned ? 'Unpin' : 'Pin'} data-id="element-2753">
                    <Pin size={14} className={ws.pinned ? 'fill-current' : ''} data-id="element-2754" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                toggleFavorite(ws.id);
              }} className={cn('p-1.5 rounded-md transition-colors', ws.favorited ? 'text-amber-400 bg-amber-400/10' : 'text-slate-400 hover:text-white hover:bg-white/[0.1]')} title={ws.favorited ? 'Unfavorite' : 'Favorite'} data-id="element-2755">
                    <Star size={14} className={ws.favorited ? 'fill-current' : ''} data-id="element-2756" />
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this workspace?')) {
                  deleteWorkspace(ws.id);
                }
              }} className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Delete" data-id="element-2757">
                    <Trash2 size={14} data-id="element-2758" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 overflow-hidden" data-id="element-2759">
                <div className="flex items-center gap-2 mb-1" data-id="element-2760">
                  <h3 className="text-sm font-semibold text-white truncate group-hover:text-[#EC4899] transition-colors" data-id="element-2761">
                    {ws.name}
                  </h3>
                  {ws.pinned && !ws.favorited && null}
                  {ws.favorited && <Star size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" data-id="element-2762" />}
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed" data-id="element-2763">
                  {ws.description || 'No description'}
                </p>
              </div>

              {/* Footer */}
              <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-slate-500" data-id="element-2764">
                <div className="flex items-center gap-1.5" data-id="element-2765">
                  <LayoutGrid size={12} data-id="element-2766" />
                  <span data-id="element-2767">
                    {ws.widgets.length} widget
                    {ws.widgets.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2768">
                  <Clock size={12} data-id="element-2769" />
                  <span data-id="element-2770">
                    {new Date(ws.updatedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                  </span>
                </div>
              </div>
            </div>)}

          {/* New Workspace Card */}
          <button onClick={handleCreate} className="jua-ws-new-card flex flex-col items-center justify-center gap-3 bg-slate-800/20 hover:bg-slate-800/40 border-2 border-dashed border-slate-700/50 hover:border-[#EC4899]/40 rounded-xl p-5 cursor-pointer h-[200px] transition-all group" data-id="element-2771">
            <div className="w-12 h-12 rounded-full bg-slate-800 group-hover:bg-[#EC4899]/10 flex items-center justify-center transition-colors" data-id="element-2772">
              <Plus size={24} className="text-slate-500 group-hover:text-[#EC4899]" data-id="element-2773" />
            </div>
            <span className="text-sm font-medium text-slate-400 group-hover:text-white" data-id="element-2774">
              Create New Workspace
            </span>
          </button>
        </div>

        {filteredWorkspaces.length === 0 && <div className="text-center py-20" data-id="element-2775">
            <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2776">
              <Search size={24} className="text-slate-500" data-id="element-2777" />
            </div>
            <h3 className="text-lg font-medium text-white mb-1" data-id="element-2778">
              No workspaces found
            </h3>
            <p className="text-slate-400" data-id="element-2779">
              Try adjusting your search or filters
            </p>
          </div>}
      </div>

      {/* New Workspace Modal */}
      {modalOpen && <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setModalOpen(false)} data-id="element-2780">
          <div className="w-full max-w-md rounded-2xl border border-white/[0.08] shadow-2xl shadow-black/60 overflow-hidden" style={{
        background: 'rgba(15, 23, 42, 0.97)',
        backdropFilter: 'blur(40px)'
      }} onClick={e => e.stopPropagation()} data-id="element-2781">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]" data-id="element-2782">
              <div data-id="element-2783">
                <h2 className="text-base font-semibold text-white" data-id="element-2784">
                  New Workspace
                </h2>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-2785">
                  {modalStep === 'choose' ? 'How would you like to start?' : 'Choose a template'}
                </p>
              </div>
              <button onClick={() => setModalOpen(false)} className="p-1.5 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors" data-id="element-2786">
                <X size={16} data-id="element-2787" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5" data-id="element-2788">
              {modalStep === 'choose' ? <div className="flex gap-3" data-id="element-2789">
                  {/* Blank Workspace */}
                  <button onClick={handleNewBlank} className="flex-1 flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06] transition-all group text-center" data-id="element-2790">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all" data-id="element-2791">
                      <Plus size={22} className="text-slate-400 group-hover:text-[#EC4899] transition-colors" data-id="element-2792" />
                    </div>
                    <div data-id="element-2793">
                      <div className="text-sm font-semibold text-white mb-1" data-id="element-2794">
                        Blank
                      </div>
                      <div className="text-[11px] text-slate-400 leading-relaxed" data-id="element-2795">
                        Start from scratch with an empty canvas
                      </div>
                    </div>
                  </button>

                  {/* Use Template */}
                  <button onClick={() => setModalStep('template')} className="flex-1 flex flex-col items-center gap-3 p-5 rounded-xl border border-white/[0.08] hover:border-[#EC4899]/40 bg-white/[0.03] hover:bg-[#EC4899]/[0.06] transition-all group text-center" data-id="element-2796">
                    <div className="w-12 h-12 rounded-xl bg-slate-800 group-hover:bg-[#EC4899]/10 border border-white/[0.06] group-hover:border-[#EC4899]/20 flex items-center justify-center transition-all" data-id="element-2797">
                      <Sparkles size={20} className="text-slate-400 group-hover:text-[#EC4899] transition-colors" data-id="element-2798" />
                    </div>
                    <div data-id="element-2799">
                      <div className="text-sm font-semibold text-white mb-1" data-id="element-2800">
                        Use Template
                      </div>
                      <div className="text-[11px] text-slate-400 leading-relaxed" data-id="element-2801">
                        Start with a pre-built widget layout
                      </div>
                    </div>
                  </button>
                </div> : <div className="space-y-2" data-id="element-2802">
                  {TEMPLATES.map(tpl => <button key={tpl.id} onClick={() => handleUseTemplate(tpl)} className="w-full flex items-center gap-4 px-4 py-3.5 rounded-xl border border-white/[0.06] hover:border-[#EC4899]/30 bg-white/[0.02] hover:bg-[#EC4899]/[0.05] transition-all group text-left" data-id="element-2803">
                      <div className="w-9 h-9 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center flex-shrink-0" data-id="element-2804">
                        {tpl.icon}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2805">
                        <div className="text-sm font-semibold text-white group-hover:text-[#EC4899] transition-colors" data-id="element-2806">
                          {tpl.name}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-2807">
                          {tpl.description}
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 flex-shrink-0" data-id="element-2808">
                        {tpl.widgets.length} widget
                        {tpl.widgets.length !== 1 ? 's' : ''}
                      </div>
                    </button>)}
                  <button onClick={() => setModalStep('choose')} className="w-full mt-1 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors" data-id="element-2809">
                    ← Back
                  </button>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}